// src/admin/pages/AdminVideos.jsx
import React, { useState, useEffect } from "react";
import { videoService, imageService } from "../../../services/api";
import { toast } from "react-toastify";

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formMode, setFormMode] = useState("add"); // 'add' or 'edit'
  const [formData, setFormData] = useState({
    imageLink: "",
    imageAlt: "",
    redirectlink: "",
    name: "",
    description: "",
  });
  const [currentId, setCurrentId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [tempVideos, setTempVideos] = useState([]);

  useEffect(() => {
    fetchVideos();
  }, []);

  // Parse imageLink string to separate link and alt
  const parseImageLink = (imageLink) => {
    if (!imageLink || imageLink.trim() === "") return { link: "", alt: "" };
    
    const parts = imageLink.split("|");
    return {
      link: parts[0] || "",
      alt: parts[1] || "",
    };
  };

  // Convert link and alt back to imageLink string
  const stringifyImageLink = (link, alt) => {
    return `${link}|${alt || ""}`;
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await videoService.getAll();
      // Sort videos by orderID
      const sortedVideos = response.data.sort((a, b) => a.orderID - b.orderID);
      setVideos(sortedVideos);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching videos:", err);
      setError("Не вдалося завантажити відео");
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Будь ласка, оберіть файл зображення");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Розмір файлу не повинен перевищувати 5MB");
      return;
    }

    try {
      setUploadingImage(true);
      const link = `videos/${Date.now()}_${file.name}`;
      const fileLink = await imageService.upload(file, link);
      
      setFormData({
        ...formData,
        imageLink: fileLink,
      });

      toast.success("Зображення успішно завантажено!");
    } catch (err) {
      console.error("Error uploading image:", err);
      toast.error("Помилка при завантаженні зображення");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const resetForm = () => {
    setFormData({
      imageLink: "",
      imageAlt: "",
      redirectlink: "",
      name: "",
      description: "",
    });
    setFormMode("add");
    setCurrentId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);

    try {
      // Combine imageLink and imageAlt
      const finalFormData = {
        ...formData,
        imageLink: stringifyImageLink(formData.imageLink, formData.imageAlt),
      };
      
      // Remove imageAlt from final data (it's now in imageLink)
      delete finalFormData.imageAlt;

      if (formMode === "add") {
        await videoService.create(finalFormData);
        toast.success("Відео успішно додано!");
      } else {
        await videoService.update(currentId, finalFormData);
        toast.success("Відео успішно оновлено!");
      }

      resetForm();
      fetchVideos();
    } catch (err) {
      console.error("Error saving video:", err);
      toast.error(`Помилка при ${formMode === "add" ? "додаванні" : "оновленні"} відео`);
    }
  };

  const handleEdit = (video) => {
    const { link, alt } = parseImageLink(video.imageLink);
    
    setFormData({
      imageLink: link,
      imageAlt: alt,
      redirectlink: video.redirectlink,
      name: video.name,
      description: video.description || "",
    });
    setCurrentId(video.orderID);
    setFormMode("edit");

    // Scroll to form
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ви впевнені, що хочете видалити це відео?")) {
      return;
    }

    try {
      await videoService.delete(id);
      setVideos(videos.filter((video) => video.orderID !== id));
      toast.success("Відео успішно видалено!");
    } catch (err) {
      console.error("Error deleting video:", err);
      toast.error("Помилка при видаленні відео");
    }
  };

  const moveUp = async (video, index) => {
    if (index === 0) return;

    try {
      const updatedVideos = [...videos];
      const itemToMove = updatedVideos[index];
      const itemAbove = updatedVideos[index - 1];
      const itemToMoveId = itemToMove.orderID;
      const itemAboveId = itemAbove.orderID;
      const updatedItemToMove = { ...itemToMove, orderID: itemAboveId };
      const updatedItemAbove = { ...itemAbove, orderID: itemToMoveId };
      updatedVideos[index] = updatedItemAbove;
      updatedVideos[index - 1] = updatedItemToMove;
      setVideos(updatedVideos);

      await Promise.all([
        videoService.update(itemToMoveId, updatedItemAbove),
        videoService.update(itemAboveId, updatedItemToMove),
      ]);

      toast.success("Порядок успішно змінено!");
      fetchVideos();
    } catch (err) {
      console.error("Error moving video up:", err);
      toast.error("Помилка при зміні порядку відео");
    }
  };

  const moveDown = async (video, index) => {
    if (index === videos.length - 1) return;

    try {
      const updatedVideos = [...videos];
      const itemToMove = updatedVideos[index];
      const itemBelow = updatedVideos[index + 1];
      const itemToMoveId = itemToMove.orderID;
      const itemBelowId = itemBelow.orderID;
      const updatedItemToMove = { ...itemToMove, orderID: itemBelowId };
      const updatedItemBelow = { ...itemBelow, orderID: itemToMoveId };
      updatedVideos[index] = updatedItemBelow;
      updatedVideos[index + 1] = updatedItemToMove;
      setVideos(updatedVideos);

      await Promise.all([
        videoService.update(itemToMoveId, updatedItemBelow),
        videoService.update(itemBelowId, updatedItemToMove),
      ]);

      toast.success("Порядок успішно змінено!");
    } catch (err) {
      console.error("Error moving video down:", err);
      toast.error("Помилка при зміні порядку відео");
    }
  };

  // Drag and drop handlers
  const reorderArray = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    setDragOverIndex(index);
    setIsDragging(true);
    setTempVideos([...videos]);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    
    if (draggedIndex === null || dragOverIndex === index) {
      return;
    }

    const reordered = reorderArray(tempVideos, dragOverIndex, index);
    setTempVideos(reordered);
    setDragOverIndex(index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dragOverIndex) {
      setIsDragging(false);
      setDraggedIndex(null);
      setDragOverIndex(null);
      setTempVideos([]);
      return;
    }

    try {
      const start = draggedIndex;
      const end = dragOverIndex;
      
      let currentVideos = [...videos];
      
      if (start < end) {
        for (let i = start; i < end; i++) {
          const item1 = currentVideos[i];
          const item2 = currentVideos[i + 1];
          const id1 = item1.orderID;
          const id2 = item2.orderID;
          
          const updatedItem1 = { ...item1, orderID: id2 };
          const updatedItem2 = { ...item2, orderID: id1 };
          
          currentVideos[i] = updatedItem2;
          currentVideos[i + 1] = updatedItem1;
          
          await Promise.all([
            videoService.update(id2, updatedItem1),
            videoService.update(id1, updatedItem2),
          ]);
        }
      } else {
        for (let i = start; i > end; i--) {
          const item1 = currentVideos[i];
          const item2 = currentVideos[i - 1];
          const id1 = item1.orderID;
          const id2 = item2.orderID;
          
          const updatedItem1 = { ...item1, orderID: id2 };
          const updatedItem2 = { ...item2, orderID: id1 };
          
          currentVideos[i] = updatedItem2;
          currentVideos[i - 1] = updatedItem1;
          
          await Promise.all([
            videoService.update(id2, updatedItem1),
            videoService.update(id1, updatedItem2),
          ]);
        }
      }
      
      toast.success("Порядок успішно змінено!");
      await fetchVideos();
    } catch (err) {
      console.error("Error reordering videos:", err);
      toast.error("Помилка при зміні порядку");
      fetchVideos();
    } finally {
      setIsDragging(false);
      setDraggedIndex(null);
      setDragOverIndex(null);
      setTempVideos([]);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setTempVideos([]);
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <h1 style={styles.pageTitle}>Слайдер з відео</h1>
        <div style={styles.loadingContainer}>
          <p>Завантаження...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.pageContainer}>
        <h1 style={styles.pageTitle}>Слайдер з відео</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.pageTitle}>Слайдер з відео</h1>

      {videos.length === 0 ? (
        <div style={styles.emptyState}>
          <h3>Немає доданих відео</h3>
          <p>Додайте відео, щоб вони з'явилися в слайдері на головній сторінці</p>
        </div>
      ) : (
        <div style={styles.videoGrid}>
          {(isDragging ? tempVideos : videos).map((video, index) => {
            const isBeingDragged = isDragging && dragOverIndex === index && draggedIndex !== null;
            const { link: imageLink, alt: imageAlt } = parseImageLink(video.imageLink);
            
            return (
              <div
                key={video.orderID}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  ...styles.videoCard,
                  opacity: isBeingDragged ? 0.4 : 1,
                  backgroundColor: isBeingDragged ? "#f0f0f0" : "white",
                  cursor: "move",
                  transform: isBeingDragged ? "scale(1.02)" : "scale(1)",
                  transition: "all 0.2s ease",
                  boxShadow: isBeingDragged ? "0 4px 12px rgba(0,0,0,0.15)" : "0 2px 5px rgba(0, 0, 0, 0.05)",
                }}
              >
                <div style={styles.dragHandle} title="Перетягніть для зміни порядку">
                  ⋮⋮
                </div>
                <div style={styles.videoThumbnail}>
                  <img src={imageLink} alt={imageAlt} style={styles.thumbnailImage} />
                </div>
                <div style={styles.videoContent}>
                  <h3 style={styles.videoTitle}>{video.name}</h3>
                  <p style={styles.videoDescription}>{video.description || "Без опису"}</p>
                  <div style={styles.videoLink}>
                    <a
                      href={video.redirectlink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.linkText}
                      title={video.redirectlink}
                    >
                      {video.redirectlink.length > 50
                        ? video.redirectlink.substring(0, 50) + "..."
                        : video.redirectlink}
                    </a>
                  </div>
                  <div style={styles.videoActions}>
                    <div style={styles.orderControls}>
                      <span style={styles.orderText}>Порядок: {index + 1}</span>
                      <div style={styles.actionButtons}>
                        <button
                          onClick={() => moveUp(video, index)}
                          disabled={index === 0 || isDragging}
                          title="Змістити вгору"
                          style={styles.iconButton}
                        >
                          ⬆️
                        </button>
                        <button
                          onClick={() => moveDown(video, index)}
                          disabled={index === videos.length - 1 || isDragging}
                          title="Змістити вниз"
                          style={styles.iconButton}
                        >
                          ⬇️
                        </button>
                      </div>
                    </div>
                    <div style={styles.actionButtons}>
                      <button
                        onClick={() => handleEdit(video)}
                        title="Редагувати"
                        style={styles.iconButton}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(video.orderID)}
                        title="Видалити"
                        style={{ ...styles.iconButton, color: "var(--error-color)" }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {formMode === "add" ? "Додати нове відео" : "Редагувати відео"}
        </h2>

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="name">
              Назва відео:
            </label>
            <input
              style={styles.input}
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Зображення превью:</label>
            
            <div style={styles.imageManager}>
              {formData.imageLink ? (
                <div style={styles.imageCard}>
                  <div 
                    style={styles.imagePreviewContainer}
                    onMouseEnter={(e) => {
                      const overlay: any = e.currentTarget.querySelector('[data-delete-overlay]');
                      if (overlay) overlay.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      const overlay: any = e.currentTarget.querySelector('[data-delete-overlay]');
                      if (overlay) overlay.style.opacity = '0';
                    }}
                  >
                    <img src={formData.imageLink} alt={formData.imageAlt} style={styles.imagePreview} />
                    <div
                      data-delete-overlay
                      style={styles.deleteOverlay}
                      onClick={() => setFormData({ ...formData, imageLink: "", imageAlt: "" })}
                    >
                      <span style={styles.deleteOverlayText}>Видалити</span>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="URL зображення"
                    value={formData.imageLink}
                    onChange={(e) => setFormData({ ...formData, imageLink: e.target.value })}
                    style={styles.imageUrlInput}
                  />
                  <input
                    type="text"
                    placeholder="Alt текст (опис)"
                    value={formData.imageAlt}
                    onChange={(e) => setFormData({ ...formData, imageAlt: e.target.value })}
                    style={styles.imageAltInput}
                  />
                </div>
              ) : (
                <input
                  type="file"
                  id="imageUpload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={styles.fileInputHidden}
                  disabled={uploadingImage}
                />
              )}
              
              {!formData.imageLink && (
                <div style={styles.addPhotoCard} onClick={() => document.getElementById("imageUpload")?.click()}>
                  <div style={styles.addPhotoButton}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    <span style={styles.addPhotoText}>
                      {uploadingImage ? "Завантаження..." : "Додати зображення"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="redirectlink">
              Посилання на відео:
            </label>
            <input
              style={styles.input}
              type="text"
              id="redirectlink"
              name="redirectlink"
              placeholder="https://youtube.com/watch?v=..."
              value={formData.redirectlink}
              onChange={handleInputChange}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="description">
              Опис (необов'язково):
            </label>
            <textarea
              style={styles.textArea}
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
            />
          </div>

          <div style={styles.buttonContainer}>
            {formMode === "edit" && (
              <button type="button" style={styles.cancelButton} onClick={resetForm}>
                Скасувати
              </button>
            )}
            <button type="submit" style={styles.button} disabled={uploadingImage}>
              {formMode === "add" ? "Додати" : "Зберегти зміни"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  formContainer: {
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  formTitle: {
    marginBottom: "1rem",
    fontSize: "1.3rem",
    color: "var(--secondary-color)",
  },
  form: {
    display: "grid",
    gap: "1rem",
    gridTemplateColumns: "minmax(0, 1fr)",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    marginBottom: "0.5rem",
    fontWeight: "600",
  },
  input: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: '"UAFSans", sans-serif',
    outline: "none",
  },
  textArea: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: '"UAFSans", sans-serif',
    minHeight: "100px",
    resize: "vertical",
    outline: "none",
  },
  button: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "green",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: '"UAFSans", sans-serif',
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  cancelButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#FFBB00",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: '"UAFSans", sans-serif',
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gridColumn: "1 / -1",
  },
  videoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "1.5rem",
    marginTop: "1rem",
    marginBottom: "3rem",
  },
  videoCard: {
    border: "1px solid #eee",
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    position: "relative",
  },
  dragHandle: {
    position: "absolute",
    top: "8px",
    left: "8px",
    fontSize: "1.2rem",
    color: "#999",
    cursor: "grab",
    userSelect: "none",
    padding: "0.25rem",
    lineHeight: 1,
    letterSpacing: "-2px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: "4px",
    zIndex: 10,
  },
  videoThumbnail: {
    position: "relative",
    paddingTop: "56.25%",
    backgroundColor: "#f0f0f0",
  },
  thumbnailImage: {
    position: "absolute",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  videoContent: {
    padding: "1rem",
    flexGrow: "1",
    display: "flex",
    flexDirection: "column",
  },
  videoTitle: {
    margin: "0 0 0.5rem",
    fontSize: "1.1rem",
    color: "var(--secondary-color)",
  },
  videoDescription: {
    margin: "0 0 0.5rem",
    color: "#666",
    fontSize: "0.9rem",
    flexGrow: "1",
  },
  videoLink: {
    marginBottom: "0.5rem",
    paddingBottom: "0.5rem",
    borderBottom: "1px solid #eee",
  },
  linkText: {
    color: "var(--primary-color)",
    textDecoration: "none",
    fontSize: "0.85rem",
    wordBreak: "break-all",
  },
  videoActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "0.5rem",
    paddingTop: "0.5rem",
    borderTop: "1px solid #eee",
  },
  actionButtons: {
    display: "flex",
    gap: "0.5rem",
  },
  iconButton: {
    background: "none",
    border: "none",
    color: "var(--primary-color)",
    cursor: "pointer",
    fontSize: "1.2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.25rem",
  },
  orderControls: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  orderText: {
    fontSize: "0.9rem",
    color: "#666",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
    marginTop: "1rem",
  },
  // Image Manager Styles
  imageManager: {
    marginTop: "0.5rem",
  },
  imageCard: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    padding: "0.75rem",
    backgroundColor: "white",
    border: "1px solid #ddd",
    borderRadius: "8px",
    maxWidth: "300px",
  },
  imagePreviewContainer: {
    position: "relative",
    width: "100%",
    height: "150px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    overflow: "hidden",
  },
  imagePreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  deleteOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.2s",
    cursor: "pointer",
  },
  deleteOverlayText: {
    backgroundColor: "white",
    color: "#333",
    padding: "0.5rem 1rem",
    borderRadius: "4px",
    fontSize: "0.9rem",
    fontWeight: 500,
    fontFamily: "'UAFSans', sans-serif",
  },
  imageUrlInput: {
    padding: "0.5rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "0.85rem",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
  },
  imageAltInput: {
    padding: "0.5rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "0.85rem",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
  },
  addPhotoCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.75rem",
    maxWidth: "300px",
    height: "150px",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: "#fafafa",
    transition: "all 0.2s",
  },
  addPhotoButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.5rem",
    color: "#666",
  },
  addPhotoText: {
    fontSize: "0.9rem",
    fontWeight: 500,
    fontFamily: "'UAFSans', sans-serif",
  },
  fileInputHidden: {
    display: "none",
  },
};

export default AdminVideos;